import classNames from 'classnames';
import { motion, Transition } from 'framer-motion';
import { cardUnits } from '../../../functions';
import { IHiddenCard, IVisibleCard } from '../../../Models/CardModels';
import { cardAriaLabel, CardSelection } from '../cardSelection';
import DraggableCard from '../DraggableCard/DraggableCard';

export interface ITableCardsProps {
  visibleCards: IVisibleCard[];
  hiddenCards: IHiddenCard[];
  selection: CardSelection;
}

const CARD_WIDTH = 62;
const BACK_OFFSET = 3;
const SLOT_WIDTH = CARD_WIDTH + BACK_OFFSET;
const MAX_CARD_STEP = 70;
const MAX_ROW_WIDTH = 214;

export const TABLE_CARD_LAYOUT_TRANSITION: Transition = {
  duration: 0.25,
  ease: 'easeOut',
};

const CARD_CLASS_NAME =
  'block max-w-none w-[calc(62*var(--card-unit))] h-[calc(90*var(--card-unit))] rounded-[5px] outline-1 -outline-offset-1 outline-black/25 shadow-table-card';

export default function TableCards({
  visibleCards,
  hiddenCards,
  selection,
}: ITableCardsProps) {
  const {
    selectedIds,
    selectedCards,
    hasSelection,
    isPlayableWith,
    canToggle,
    isRejected,
    tapCard,
  } = selection;

  const slotCount = Math.max(visibleCards.length, hiddenCards.length);
  if (slotCount === 0) {
    return null;
  }

  const cardStep = Math.min(
    MAX_CARD_STEP,
    (MAX_ROW_WIDTH - SLOT_WIDTH) / Math.max(slotCount - 1, 1)
  );
  const rowWidth = (slotCount - 1) * cardStep + SLOT_WIDTH;
  const slots = Array.from({ length: slotCount }, (_, i) => i);

  const renderHiddenCard = (
    hiddenCard: IHiddenCard,
    left: number,
    isCovered: boolean,
    isDimmed: boolean
  ) => (
    <motion.div
      key={hiddenCard.card.cardId}
      layoutId={hiddenCard.card.cardId}
      transition={TABLE_CARD_LAYOUT_TRANSITION}
      className={classNames('absolute flex transition-[filter] duration-150', {
        'brightness-60': isDimmed,
      })}
      style={{ left: cardUnits(left), top: cardUnits(BACK_OFFSET) }}
    >
      <DraggableCard
        card={hiddenCard}
        cardClassName={CARD_CLASS_NAME}
        disabled={isCovered}
      />
    </motion.div>
  );

  const renderVisibleCard = (
    visibleCard: IVisibleCard,
    left: number,
    isDimmed: boolean
  ) => {
    const cardId = visibleCard.card.cardId;
    const isSelected = selectedIds.includes(cardId);
    return (
      <motion.div
        key={cardId}
        layoutId={cardId}
        transition={TABLE_CARD_LAYOUT_TRANSITION}
        className={classNames(
          'absolute flex transition-[filter] duration-150',
          { 'brightness-60': isDimmed }
        )}
        style={{ left: cardUnits(left), top: 0 }}
      >
        <DraggableCard
          card={visibleCard}
          cardClassName={classNames(CARD_CLASS_NAME, {
            'ring-3 ring-accent-fill': isSelected,
          })}
          dragGroup={isSelected ? selectedCards : undefined}
          isRejected={isRejected(cardId)}
          ariaLabel={cardAriaLabel(
            visibleCard,
            isSelected,
            isPlayableWith([cardId])
          )}
          onClick={() => tapCard(cardId)}
        />
      </motion.div>
    );
  };

  return (
    <section
      aria-label="Your table"
      className="mt-auto flex items-center justify-center gap-[14px] px-[12px]"
    >
      <div className="flex flex-col gap-[3px] rounded-[12px] bg-surface px-[12px] py-[9px] text-ink inset-ring inset-ring-hairline shadow-soft">
        <span className="text-[13px] font-bold">Your table</span>
        <span className="text-[12px] font-medium text-ink-muted">
          Played after your hand
        </span>
      </div>
      <div
        className="relative h-[calc(93*var(--card-unit))]"
        style={{ width: cardUnits(rowWidth) }}
      >
        {slots.flatMap((i) => {
          const topCard = visibleCards[i] ?? hiddenCards[i];
          const isDimmed = hasSelection && !canToggle(topCard.card.cardId);
          return [
            hiddenCards[i] &&
              renderHiddenCard(
                hiddenCards[i],
                i * cardStep + BACK_OFFSET,
                i < visibleCards.length,
                isDimmed
              ),
            visibleCards[i] &&
              renderVisibleCard(visibleCards[i], i * cardStep, isDimmed),
          ];
        })}
      </div>
    </section>
  );
}
